import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BarChart3,
  Users,
  Building2,
  CreditCard,
  Settings,
  Upload,
  TrendingUp,
  User,
  Lock,
  UserCheck,
  ChevronDown,
  ChevronRight,
  Calendar,
  Building,
  UserCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  path: string;
  roles?: string[];
  badge?: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

interface SidebarProps {
  onItemClick?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ onItemClick }) => {
  const { user } = useAuth();
  const location = useLocation();
  const currentPath = location.pathname;

  // Check if user has required role
  const hasRole = (roles?: string[]) => {
    if (!roles) return true;
    return user?.role && roles.includes(user.role);
  };

  const navigationSections: NavSection[] = [
    {
      title: 'Overview',
      items: [
        {
          id: 'dashboard',
          label: 'Dashboard',
          icon: BarChart3,
          path: '/dashboard'
        }
      ]
    },
    {
      title: 'Data Management',
      items: [
        {
          id: 'csv-upload',
          label: 'CSV Upload',
          icon: Upload,
          path: '/csv-upload'
        },
        {
          id: 'transactions',
          label: 'Transactions',
          icon: CreditCard,
          path: '/transactions'
        },
        {
          id: 'manual-checks',
          label: 'Manual Checks',
          icon: CreditCard,
          path: '/manual-checks'
        }
      ]
    },
    {
      title: 'Account Management',
      items: [
        {
          id: 'accounts',
          label: 'Accounts',
          icon: Building2,
          path: '/accounts'
        },
        {
          id: 'clients',
          label: 'Clients',
          icon: Users,
          path: '/clients'
        },
        {
          id: 'employees',
          label: 'Employees',
          icon: UserCheck,
          path: '/employees'
        }
      ]
    },
    {
      title: 'Configuration',
      items: [
        {
          id: 'account-matching',
          label: 'Account Matching',
          icon: Settings,
          path: '/account-matching'
        },
        {
          id: 'commission-presets',
          label: 'Commission Presets',
          icon: TrendingUp,
          path: '/commission-presets'
        }
      ]
    },
    {
      title: 'Reports',
      items: [
        {
          id: 'blotter-report',
          label: 'Blotter Report',
          icon: Calendar,
          path: '/reports/blotter'
        },
        {
          id: 'user-report',
          label: 'User Report',
          icon: UserCircle,
          path: '/reports/user'
        },
        {
          id: 'client-report',
          label: 'Client Report',
          icon: Users,
          path: '/reports/client'
        },
        {
          id: 'account-report',
          label: 'Account Report',
          icon: Building,
          path: '/reports/account'
        },
        {
          // R17 Task H (Peter #2a): Check Report by Firm — aggregates
          // manual checks by firm across all salesmen for a date range.
          id: 'check-report',
          label: 'Check Report',
          icon: CreditCard,
          path: '/reports/checks'
        }
      ]
    },
    {
      title: 'Administration',
      items: [
        {
          id: 'users',
          label: 'Users',
          icon: Users,
          path: '/admin/users',
          roles: ['super_admin'],
          badge: 'Admin'
        }
      ]
    },
    {
      title: 'My Account',
      items: [
        {
          id: 'profile',
          label: 'My Profile',
          icon: User,
          path: '/profile'
        },
        {
          id: 'change-password',
          label: 'Change Password',
          icon: Lock,
          path: '/change-password'
        }
      ]
    }
  ];

  const isActive = (path: string) => {
    return currentPath === path || currentPath.startsWith(path + '/');
  };

  // Find which section contains the active page
  const getActiveSectionTitle = () => {
    for (const section of navigationSections) {
      const visibleItems = section.items.filter(item => hasRole(item.roles));
      if (visibleItems.some(item => isActive(item.path))) {
        return section.title;
      }
    }
    return null;
  };

  const activeSectionTitle = getActiveSectionTitle();

  // State to track manually toggled sections
  const [manuallyExpandedSections, setManuallyExpandedSections] = React.useState<Record<string, boolean>>({});

  // Toggle section expansion
  const toggleSection = (sectionTitle: string) => {
    setManuallyExpandedSections(prev => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle]
    }));
  };

  // Determine if a section should be expanded
  const isSectionExpanded = (sectionTitle: string) => {
    // If user manually toggled it, use that state
    if (manuallyExpandedSections.hasOwnProperty(sectionTitle)) {
      return manuallyExpandedSections[sectionTitle];
    }
    // Otherwise, expand only if it contains the active page
    return sectionTitle === activeSectionTitle;
  };

  return (
    <div className="w-64 bg-white shadow-sm border-r border-gray-200 h-full overflow-y-auto flex flex-col">
      <div className="p-4">
        <div className="flex items-center space-x-3 mb-6 lg:mb-8">
          <BarChart3 className="h-8 w-8 text-primary-600" />
          <div>
            <h1 className="text-lg font-semibold text-gray-900">MCH Commission</h1>
            <p className="text-xs text-gray-500">Trading System</p>
          </div>
        </div>

        <nav className="space-y-2">
          {navigationSections.map((section) => {
            const visibleItems = section.items.filter(item => hasRole(item.roles));

            if (visibleItems.length === 0) return null;

            const isExpanded = isSectionExpanded(section.title);
            const hasActiveItem = visibleItems.some(item => isActive(item.path));

            return (
              <div key={section.title}>
                {/* Section Header - Clickable to expand/collapse */}
                <button
                  onClick={() => toggleSection(section.title)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-lg transition-colors ${hasActiveItem
                    ? 'text-primary-700 bg-primary-50'
                    : 'text-gray-500 hover:bg-gray-50'
                    }`}
                >
                  <span>{section.title}</span>
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>

                {/* Collapsible Menu Items */}
                {isExpanded && (
                  <ul className="space-y-1 mt-2 ml-2">
                    {visibleItems.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.path);

                      return (
                        <li key={item.id}>
                          <Link
                            to={item.path}
                            onClick={onItemClick}
                            className={`flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${active
                              ? 'bg-primary-50 text-primary-700 border-l-2 border-primary-600'
                              : 'text-gray-700 hover:bg-gray-100 active:bg-gray-200'
                              }`}
                          >
                            <div className="flex items-center space-x-3">
                              <Icon className={`h-5 w-5 ${active ? 'text-primary-600' : 'text-gray-500'}`} />
                              <span className="font-medium">{item.label}</span>
                            </div>
                            {item.badge && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                                {item.badge}
                              </span>
                            )}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      {/* User Info at Bottom */}
      <div className="mt-auto p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-medium">
              {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.name || 'User'}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {user?.email}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};